import {
  durationBucketForMs,
  emitChromeViewerLifecycle,
  generateChromeViewerId,
  retryOrdinalForCount,
  statusClassForStatus,
} from './chromeViewerTelemetry';
import { requestOwnsRouteGeneration } from './chromeViewerRequestOwnership';
import SingleFlightRequest from './singleFlightRequest';

const isUsableChromeViewerSignedUrl = (signedUrl) => {
  if (typeof signedUrl !== 'string' || !signedUrl || signedUrl.trim() !== signedUrl) {
    return false;
  }

  try {
    return new URL(signedUrl).protocol === 'https:';
  } catch {
    return false;
  }
};

class ChromeViewerRequestLifecycle {
  constructor(loadRecord) {
    this.loadRecord = loadRecord;
    this.routeGeneration = generateChromeViewerId();
    this.retryOrdinal = 0;
    this.activeRequest = null;
    this.selectionFetch = new SingleFlightRequest();
    this.mounted = false;
    this.retryPending = false;
    this.annotationSave = null;
  }

  mount = () => {
    this.mounted = true;
    emitChromeViewerLifecycle('component_mounted');
  };

  unmount = () => {
    this.mounted = false;
    this.abort();
  };

  abort = () => {
    this.activeRequest?.abortController.abort();
    this.selectionFetch.abort();
    this.activeRequest = null;
    this.retryPending = false;
    if (this.annotationSave?.pending) {
      this.annotationSave = {
        ...this.annotationSave,
        pending: false,
        settled: true,
      };
    }
  };

  resetRoute = () => {
    this.abort();
    this.retryOrdinal = 0;
    this.routeGeneration = generateChromeViewerId();
  };

  owns = (request, routeId) => (
    requestOwnsRouteGeneration(request, routeId, this.routeGeneration)
  );

  presentation = (request, routeId, signedUrl) => {
    if (!this.owns(request, routeId) || request.loading || !request.settled) return 'loading';
    if (request.error) return 'failure';
    if (request.completed && isUsableChromeViewerSignedUrl(signedUrl)) return 'ready';
    if (request.completed) return 'missing';
    return 'loading';
  };

  load = async (routeId, { retryClicked = false } = {}) => {
    if (!this.mounted) return null;

    const ownership = {
      attemptToken: generateChromeViewerId(),
      routeOwnerId: routeId,
      routeGeneration: this.routeGeneration,
    };
    const abortController = new AbortController();
    const fetchId = generateChromeViewerId();
    const request = {
      ...ownership,
      abortController,
      fetchId,
      retryOrdinal: this.retryOrdinal,
      startedAt: Date.now(),
    };
    this.activeRequest = request;
    this.retryPending = false;

    if (retryClicked) {
      emitChromeViewerLifecycle('record_retry_clicked', {
        fetch_id: fetchId,
        retry_ordinal: request.retryOrdinal,
      });
    }
    emitChromeViewerLifecycle('record_fetch_started', {
      fetch_id: fetchId,
      retry_ordinal: request.retryOrdinal,
    });

    try {
      const result = await this.loadRecord(routeId, {
        signal: abortController.signal,
        requestOwnership: ownership,
      });
      const terminalFields = {
        fetch_id: fetchId,
        retry_ordinal: request.retryOrdinal,
        duration_bucket: durationBucketForMs(Date.now() - request.startedAt),
      };

      if (abortController.signal.aborted || result?.canceled || result?.cancelled) {
        emitChromeViewerLifecycle('record_fetch_cancelled', {
          ...terminalFields,
          terminal_outcome: 'cancelled',
        });
      } else if (result?.error) {
        emitChromeViewerLifecycle('record_fetch_failed', {
          ...terminalFields,
          terminal_outcome: 'failed',
          status_class: statusClassForStatus(result.response?.status),
        });
      } else if (!isUsableChromeViewerSignedUrl(result?.data?.signed_url)) {
        emitChromeViewerLifecycle('record_fetch_failed', {
          ...terminalFields,
          terminal_outcome: 'failed',
          status_class: statusClassForStatus(result?.status, { missingContent: true }),
        });
      } else {
        emitChromeViewerLifecycle('record_fetch_succeeded', {
          ...terminalFields,
          terminal_outcome: 'succeeded',
        });
      }

      return result;
    } finally {
      if (this.activeRequest?.attemptToken === ownership.attemptToken) {
        this.activeRequest = null;
      }
    }
  };

  retry = (routeId, presentation) => {
    if (this.activeRequest || this.retryPending || presentation === 'loading') return null;

    this.retryPending = true;
    this.retryOrdinal = retryOrdinalForCount(this.retryOrdinal + 1);
    return this.load(routeId, { retryClicked: true });
  };

  selectionIsBusy = () => (
    this.selectionFetch.isBusy() || Boolean(this.annotationSave?.pending)
  );

  startSelectionFetch = (ownership) => {
    if (this.selectionIsBusy()) return null;

    return this.selectionFetch.start(ownership.attemptToken)?.signal || null;
  };

  finishSelectionFetch = (ownership) => {
    this.selectionFetch.finish(ownership.attemptToken);
  };

  startAnnotationSave = (ownership) => {
    if (this.selectionIsBusy()) return null;

    const operation = { ownership };
    this.annotationSave = {
      operation,
      pending: true,
      settled: false,
    };
    return operation;
  };

  annotationSaveCanDispatch = (operation, ownershipIsCurrent) => (
    this.annotationSave?.operation === operation
    && this.annotationSave.pending
    && !this.annotationSave.settled
    && ownershipIsCurrent
  );

  annotationSaveIsCurrent = (operation) => this.annotationSave?.operation === operation;

  markAnnotationSaveTerminalDispatched = (operation) => {
    if (!this.annotationSaveIsCurrent(operation)) return;

    this.annotationSave = {
      ...this.annotationSave,
      settled: true,
    };
  };

  finishAnnotationSave = (operation) => {
    if (!this.annotationSaveIsCurrent(operation)) return;

    this.annotationSave = {
      ...this.annotationSave,
      pending: false,
      settled: true,
    };
  };
}

export { isUsableChromeViewerSignedUrl };
export default ChromeViewerRequestLifecycle;
